/**
 * MechaGuard - CustomMaps (自定义地图的校验、参数推算与持久化)
 *
 * 职责：
 *   1. 把编辑器产出的「草稿」校验成合法关卡，或给出可读的错误列表
 *   2. 按路径长度自动推算起始金币 / 生命 / 难度倍率
 *   3. 持久化到 localStorage，并把结果同步进 MapData 的运行时注册表
 *
 * 两条关键设计取舍
 * ----------------
 * **一、参数全部自动推算，不给玩家调。**
 * 自定义地图没法人工平衡。如果能自由设定起始金币，玩家可以造一张
 * 「开局满钱 + 超长绕圈」的图来刷分。
 *
 * **二、参数在「读取存档」时重新推算一遍，不信任存档里的值。**
 * 否则手改 localStorage 就能塞进 startGold: 99999。重新推算等于把
 * 这条路堵死 —— 路径是玩家画的，但代价由公式决定。
 *
 * 加上「自定义地图不计入排行榜」（见 Game._finalizeRun），三处一起
 * 构成完整闭环。
 */

import { readJson, writeJson } from '../systems/Storage.js';
import {
  COLS,
  ROWS,
  validateLevel,
  pathTileLength,
  registerCustomLevel,
  clearCustomLevels,
} from './MapData.js';

/** 存档键。带版本号，将来结构变了可以平滑迁移。 */
export const CUSTOM_MAP_KEY = 'mechguard.custommaps.v1';

/** 最多保存多少张（防止档案无限膨胀挤爆 localStorage 配额） */
export const MAX_CUSTOM_MAPS = 20;

/** 地图名长度上限 */
export const NAME_MAX = 16;

/** 拐点数量上限（防止一条折线塞进上千个点把渲染拖垮） */
export const MAX_WAYPOINTS = 64;

/** 出怪口可以贴靠的两条边 */
export const SPAWN_EDGES = Object.freeze({
  LEFT: 'left',
  RIGHT: 'right',
});

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const round2 = v => Math.round(v * 100) / 100;

/* ==========================================================
 * 参数推算
 * ========================================================== */

/**
 * 按路径长度推算关卡参数。
 *
 * 核心直觉：**路径越长，敌人暴露在火力下的时间越久，对玩家越有利**。
 * 所以路径变长时必须同步提高敌人强度，并且让启动资金涨得比路径慢 ——
 * 否则一张「绕满全图的长蛇」会变成白给局。
 *
 * 系数以内置「前哨站」（31 格）为基准校准，让一张中等长度的地图
 * 落在与内置关卡相近的手感上。
 *
 * @param {{x:number,y:number}[]} path
 */
export function deriveLevelParams(path) {
  const tiles = pathTileLength(path);

  return {
    tileLength: tiles,
    startGold: Math.round(clamp(120 + tiles * 2.6, 150, 420)),
    startLives: tiles >= 42 ? 8 : 10,
    totalWaves: 30,
    hpScaleMul: round2(clamp(0.6 + tiles * 0.013, 0.8, 1.5)),
    speedMul: round2(clamp(0.9 + tiles * 0.003, 0.9, 1.15)),
    // difficulty 只用于展示与卡片星级，真正的难度体现在上面两个倍率
    difficulty: tiles < 28 ? 1 : (tiles < 40 ? 2 : 3),
  };
}

/* ==========================================================
 * 校验
 * ========================================================== */

/**
 * 出怪口落点是否合法。
 *
 * 只允许贴靠左右两条边（与内置三关一致）。上下入场需要放宽
 * validateLevel() 里的行越界检查，会牵动已有测试，暂不支持。
 *
 * @param {{x:number,y:number}} p
 */
export function isValidSpawnPoint(p) {
  if (!p) return false;
  if (p.y < 0 || p.y >= ROWS) return false;
  return p.x === -1 || p.x === COLS;
}

/**
 * 校验一份草稿。
 *
 * 在 validateLevel()（轴对齐 / 不自交 / 基地在网格内）之上，
 * 补充自定义地图特有的约束。
 *
 * @param {{name?:string, path?:{x:number,y:number}[]}} draft
 * @returns {{ok:boolean, errors:string[], tileLength:number}}
 */
export function validateDraft(draft) {
  const errors = [];

  if (!draft || typeof draft !== 'object') {
    return { ok: false, errors: ['草稿数据无效'], tileLength: 0 };
  }

  const name = typeof draft.name === 'string' ? draft.name.trim() : '';
  if (!name) {
    errors.push('请先给地图起个名字');
  } else if (name.length > NAME_MAX) {
    errors.push(`名字不能超过 ${NAME_MAX} 个字`);
  }

  const path = Array.isArray(draft.path) ? draft.path : null;

  if (!path || path.length < 2) {
    errors.push('至少需要 2 个拐点才能形成一条通道');
    return { ok: false, errors, tileLength: 0 };
  }

  if (path.length > MAX_WAYPOINTS) {
    errors.push(`拐点太多（${path.length}），最多 ${MAX_WAYPOINTS} 个`);
  }

  // 拐点必须是整数格坐标（小数点坐标会让路径格判定出现半格偏移）
  for (let i = 0; i < path.length; i++) {
    const p = path[i];
    if (!p || !Number.isInteger(p.x) || !Number.isInteger(p.y)) {
      errors.push(`第 ${i + 1} 个拐点不是整数格坐标`);
      break;
    }
  }

  // 除出怪口外，所有拐点都必须落在网格内。
  // 越界的中间点会让敌人走出画面再走回来，路径格标记也会被 createMap
  // 静默丢弃，视觉与逻辑就对不上了。
  for (let i = 1; i < path.length; i++) {
    const p = path[i];
    if (!p || !Number.isInteger(p.x) || !Number.isInteger(p.y)) continue;
    if (p.x < 0 || p.x >= COLS || p.y < 0 || p.y >= ROWS) {
      errors.push(`第 ${i + 1} 个拐点越界: (${p.x},${p.y})`);
      break;
    }
  }

  if (errors.length === 0) {
    // 上面两项通过后再交给内置自检，避免它拿到脏数据
    errors.push(...validateLevel({ path }).errors);
  }

  if (!isValidSpawnPoint(path[0])) {
    errors.push('出怪口必须贴在左边缘或右边缘的画面外一格');
  }

  const tileLength = pathTileLength(path);
  if (tileLength < 8) {
    errors.push(`通道太短（${tileLength} 格），至少需要 8 格`);
  }

  return { ok: errors.length === 0, errors, tileLength };
}

/* ==========================================================
 * 关卡对象构建
 * ========================================================== */

/** 由路径与名字组装出一个标准关卡对象（结构与内置关卡一致） */
export function buildLevel({ id, name, path, createdAt, updatedAt }) {
  const params = deriveLevelParams(path);
  const now = Date.now();

  return {
    id,
    name: name.trim().slice(0, NAME_MAX),
    subtitle: `自定义 · 通道 ${params.tileLength} 格`,
    icon: '🗺️',
    difficulty: params.difficulty,

    // 复制一份，避免调用方后续改动草稿影响已保存的数据
    path: path.map(p => ({ x: p.x, y: p.y })),

    startGold: params.startGold,
    startLives: params.startLives,
    totalWaves: params.totalWaves,
    hpScaleMul: params.hpScaleMul,
    speedMul: params.speedMul,

    // 自定义地图标记：Game._finalizeRun 用它决定是否提交排行榜
    custom: true,
    tileLength: params.tileLength,
    createdAt: typeof createdAt === 'number' ? createdAt : now,
    updatedAt: typeof updatedAt === 'number' ? updatedAt : now,
  };
}

/**
 * 规范化一条存档记录。
 *
 * **参数一律重新推算**，不采用存档里写的值 —— 手改 localStorage
 * 就能造出「起始金币 99999」的地图，重新推算把这条路堵死。
 *
 * @returns {object|null} 非法记录返回 null（由调用方丢弃）
 */
export function normalizeRecord(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const id = typeof raw.id === 'string' ? raw.id : '';
  if (!/^custom-[a-z0-9-]+$/i.test(id)) return null;

  const name = typeof raw.name === 'string'
    ? raw.name.trim().slice(0, NAME_MAX)
    : '';
  if (!name) return null;

  if (!Array.isArray(raw.path)
    || raw.path.length < 2
    || raw.path.length > MAX_WAYPOINTS) {
    return null;
  }

  const path = [];
  for (const p of raw.path) {
    if (!p || !Number.isInteger(p.x) || !Number.isInteger(p.y)) return null;
    // 折线允许首点越界一格，其余点收敛在网格内
    if (p.x < -1 || p.x > COLS || p.y < -1 || p.y > ROWS) return null;
    path.push({ x: p.x, y: p.y });
  }

  if (!validateDraft({ name, path }).ok) return null;

  return buildLevel({
    id,
    name,
    path,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  });
}

/* ==========================================================
 * 仓库
 * ========================================================== */

let idCounter = 0;

/** 生成一个不会与内置关卡（outpost / canyon / reactor）冲突的 id */
function generateId() {
  idCounter += 1;
  return `custom-${Date.now().toString(36)}-${idCounter.toString(36)}`;
}

export class CustomMapStore {
  /**
   * @param {{storage: object}} opts storage 为 Storage.js 的后端
   */
  constructor({ storage }) {
    this.storage = storage;
    /** @type {object[]} */
    this.maps = [];
    this.load();
  }

  /** 从存储读取并规范化；损坏的记录直接丢弃，不影响其余地图 */
  load() {
    const raw = readJson(this.storage, CUSTOM_MAP_KEY, null);
    const list = raw && Array.isArray(raw.maps) ? raw.maps : [];

    this.maps = [];
    for (const record of list) {
      const level = normalizeRecord(record);
      if (level) this.maps.push(level);
    }

    this.syncRegistry();
    return this.maps;
  }

  /** 把当前地图注册进 MapData，让 getLevel()/createMap() 能找到它们 */
  syncRegistry() {
    clearCustomLevels();
    for (const map of this.maps) registerCustomLevel(map);
  }

  _persist() {
    return writeJson(this.storage, CUSTOM_MAP_KEY, {
      version: 1,
      maps: this.maps,
    });
  }

  /** 全部地图，最近更新在前 */
  list() {
    return this.maps.slice().sort((a, b) => b.updatedAt - a.updatedAt);
  }

  get(id) {
    return this.maps.find(m => m.id === id) || null;
  }

  get count() {
    return this.maps.length;
  }

  get isFull() {
    return this.maps.length >= MAX_CUSTOM_MAPS;
  }

  /**
   * 保存一份草稿。draft.id 指向已有地图时是覆盖，否则新建。
   * @returns {{ok:boolean, errors:string[], level?:object}}
   */
  save(draft) {
    const name = draft && typeof draft.name === 'string' ? draft.name.trim() : '';
    const path = draft && Array.isArray(draft.path) ? draft.path : [];

    const check = validateDraft({ name, path });
    if (!check.ok) return { ok: false, errors: check.errors };

    const existing = draft.id ? this.get(draft.id) : null;

    if (!existing && this.isFull) {
      return {
        ok: false,
        errors: [`最多保存 ${MAX_CUSTOM_MAPS} 张地图，请先删除一些`],
      };
    }

    const level = buildLevel({
      id: existing ? existing.id : generateId(),
      name,
      path,
      // 覆盖时保留原始创建时间
      createdAt: existing ? existing.createdAt : Date.now(),
      updatedAt: Date.now(),
    });

    if (existing) {
      this.maps[this.maps.indexOf(existing)] = level;
    } else {
      this.maps.push(level);
    }

    this._persist();
    this.syncRegistry();

    return { ok: true, errors: [], level };
  }

  /** @returns {boolean} 是否真的删掉了东西 */
  remove(id) {
    const index = this.maps.findIndex(m => m.id === id);
    if (index < 0) return false;

    this.maps.splice(index, 1);
    this._persist();
    this.syncRegistry();
    return true;
  }

  clear() {
    this.maps = [];
    this._persist();
    this.syncRegistry();
  }
}
